import { z } from 'zod';
import { count, isoDate, isoTimestamp, ratio } from './primitives.js';
/*
 * GET /booking-funnel — the own-booking widget's session analytics
 * (booking_events): message/offer exposure immediately before the modal plus
 * what happens INSIDE it, the black box between lp_modal_open and lp_booked.
 * Modal open → calendar → slot → details → submit → booked, with error and
 * empty-calendar counters.
 *
 * Privacy by construction: rows carry step/slot metadata only — never a
 * name/phone/email. The person lives on the bookings row `refCode` joins to.
 */
const ok = z.literal(true);
/** Public, allowlisted experiment dimensions; none assert verified identity. */
export const audienceJourneySchema = z.enum(['control_50_plus', 'antigym_25_49']);
export const offerVersionSchema = z.enum(['baseline-749-899']);
export const offerSurfaceSchema = z.enum([
    'hero-price-slide',
    'two-plan-choice',
    'membership-plans',
]);
export const planIntentSchema = z.enum(['annual-749', 'flex-899', 'annual-upfront-8990']);
/** The exact ad behind the session (parsed from the tagged URL). */
export const bookingFunnelAdSchema = z.object({
    adName: z.string().nullable(), // utm_content = {{ad.name}}
    adId: z.string().nullable(),
    campaign: z.string().nullable(),
});
/** One beacon in a session's journey (chronological). */
export const bookingFunnelJourneyEventSchema = z.object({
    at: isoTimestamp,
    event: z.string(),
    stepName: z.string().nullable(),
    slotsShown: z.number().nullable(),
    slotIso: z.string().nullable(),
    errorKind: z.string().nullable(),
    offerSurface: offerSurfaceSchema.nullable(),
    planIntent: planIntentSchema.nullable(),
    /** day_select: the date tapped, and how deep in the strip it sat. */
    dayIso: z.string().nullable(),
    dayIndex: z.number().nullable(),
});
/** Farthest funnel stage a session reached, in order. */
export const bookingFunnelStageSchema = z.enum([
    'opened', // modal_open / flow_start only
    'saw_calendar', // calendar_view with slots > 0
    'selected_slot',
    'opened_details',
    'attempted',
    'booked',
]);
/** Which page hosted the widget. */
export const bookingFunnelSourceSchema = z.enum(['quiz', 'lp', 'other']);
/** One booking-widget session, aggregated from its beacons. */
export const bookingFunnelSessionSchema = z.object({
    sessionId: z.string(),
    /** Same-tab quiz session (nono_quiz_sid) when opened from the quiz page. */
    quizSessionId: z.string().nullable(),
    startedAt: isoTimestamp,
    lastSeenAt: isoTimestamp,
    durationSec: count,
    device: z.enum(['mobile', 'desktop', 'unknown']),
    hasFbclid: z.boolean(),
    ad: bookingFunnelAdSchema,
    source: bookingFunnelSourceSchema,
    audienceJourney: audienceJourneySchema.nullable(),
    offerVersion: offerVersionSchema.nullable(),
    offerSurfaces: z.array(offerSurfaceSchema),
    planIntent: planIntentSchema.nullable(),
    stage: bookingFunnelStageSchema,
    /** Chosen slot (last slot_select / booked). */
    slotIso: z.string().nullable(),
    /** Booking ref on success — joins the bookings row (the person). */
    refCode: z.string().nullable(),
    /** calendar_view rows that rendered ZERO bookable slots. */
    sawEmptyCalendar: z.boolean(),
    errorKinds: z.array(z.string()),
    /** Widget step at the last `closed` beacon (where they left), if any. */
    closedAtStep: z.string().nullable(),
    journey: z.array(bookingFunnelJourneyEventSchema),
});
/** Per-source funnel rollup (quiz page vs landing page). */
export const bookingFunnelBySourceSchema = z.object({
    source: bookingFunnelSourceSchema,
    sessions: count,
    selectedSlot: count,
    attempted: count,
    booked: count,
});
/** Consented journey_view cohort, separated by message and offer. */
export const bookingFunnelByJourneySchema = z.object({
    audienceJourney: audienceJourneySchema.nullable(),
    offerVersion: offerVersionSchema.nullable(),
    sessions: count,
    opened: count,
    selectedSlot: count,
    attempted: count,
    booked: count,
});
/** Last selected plan within the consented journey_view cohort. */
export const bookingFunnelByPlanIntentSchema = z.object({
    planIntent: planIntentSchema.nullable(),
    offerVersion: offerVersionSchema.nullable(),
    sessions: count,
    opened: count,
    selectedSlot: count,
    attempted: count,
    booked: count,
});
/** Consented sessions per offer placement; sessions may appear in several rows. */
export const bookingFunnelByOfferSurfaceSchema = z.object({
    offerSurface: offerSurfaceSchema.nullable(),
    offerVersion: offerVersionSchema.nullable(),
    sessions: count,
    opened: count,
    selectedSlot: count,
    attempted: count,
    booked: count,
});
export const bookingFunnelResponse = z.object({
    ok,
    from: isoDate,
    to: isoDate,
    totals: z.object({
        sessions: count,
        sawCalendar: count, // calendar_view with slots > 0
        selectedSlot: count,
        openedDetails: count,
        attempted: count, // valid submit fired
        booked: count,
        /** booked ÷ sessions. */
        completionPct: ratio,
        /** selectedSlot ÷ sawCalendar. */
        slotPct: ratio,
        /** booked ÷ openedDetails (the form's own conversion). */
        detailsPct: ratio,
        /** Sessions shown an EMPTY calendar (slotsShown = 0) — availability alarm. */
        emptyCalendarSessions: count,
        /** Sessions whose availability fetch failed outright. */
        calendarErrorSessions: count,
        /** submit_error counts by kind (validation | slot_taken | server | network). */
        errors: z.record(z.string(), count),
    }),
    bySource: z.array(bookingFunnelBySourceSchema),
    byJourney: z.array(bookingFunnelByJourneySchema),
    byPlanIntent: z.array(bookingFunnelByPlanIntentSchema),
    byOfferSurface: z.array(bookingFunnelByOfferSurfaceSchema),
    /**
     * Day-hunting summary — the whole point of instrumenting day_select. Answers
     * "are they looking for a weekend / a date far ahead, or just toggling?"
     */
    dayProbe: z.object({
        /** day_select taps grouped by weekday (1=Mon … 7=Sun). */
        byWeekday: z.array(z.object({ weekday: count, taps: count })),
        /** How deep into the strip people tap (0 = first day offered). */
        maxIndexReached: z.object({
            p50: z.number().nullable(),
            p90: z.number().nullable(),
            max: z.number().nullable(),
        }),
        /** Sessions that browsed past this many days without picking any slot. */
        deepHuntersNoPick: count,
        /** Total day taps in range (context for the numbers above). */
        taps: count,
    }),
    /** Newest sessions first, capped server-side. */
    sessions: z.array(bookingFunnelSessionSchema),
});
//# sourceMappingURL=booking-funnel.js.map